import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Plus, 
  Search, 
  FolderKanban, 
  Calendar as CalendarIcon,
  Loader2,
  MoreVertical,
  Eye,
  Pencil,
  Trash2,
  Globe,
  Code,
  TrendingUp,
  Palette,
  Megaphone,
  LayoutTemplate,
  Tag,
  X,
  Activity
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' }
];

const projectTypes = [
  { value: 'web_design', label: 'Web Design', icon: Globe, color: 'text-blue-600 bg-blue-50' },
  { value: 'seo', label: 'SEO', icon: TrendingUp, color: 'text-green-600 bg-green-50' },
  { value: 'software_development', label: 'Software Development', icon: Code, color: 'text-purple-600 bg-purple-50' },
  { value: 'branding', label: 'Branding', icon: Palette, color: 'text-orange-600 bg-orange-50' },
  { value: 'marketing', label: 'Marketing', icon: Megaphone, color: 'text-pink-600 bg-pink-50' },
  { value: 'dme_medical', label: 'DME Medical', icon: Activity, color: 'text-red-600 bg-red-50' },
  { value: 'other', label: 'Other', icon: LayoutTemplate, color: 'text-slate-600 bg-slate-50' }
];

// Project type specific field templates
const projectTemplates = {
  web_design: {
    fields: [
      { name: 'num_pages', label: 'Number of Pages', type: 'number', placeholder: 'e.g., 5' },
      { name: 'cms_platform', label: 'CMS Platform', type: 'select', options: ['WordPress', 'Webflow', 'Shopify', 'Custom', 'None'] },
      { name: 'responsive', label: 'Responsive Design', type: 'boolean', default: true },
      { name: 'design_style', label: 'Design Style', type: 'select', options: ['Modern', 'Minimalist', 'Corporate', 'Creative', 'E-commerce'] },
      { name: 'has_ecommerce', label: 'E-commerce Features', type: 'boolean', default: false },
      { name: 'hosting_provider', label: 'Hosting Provider', type: 'text', placeholder: 'e.g., AWS, GoDaddy' },
    ]
  },
  seo: {
    fields: [
      { name: 'target_keywords', label: 'Target Keywords', type: 'textarea', placeholder: 'Enter primary keywords (comma separated)' },
      { name: 'competitor_urls', label: 'Competitor URLs', type: 'textarea', placeholder: 'Enter competitor URLs (one per line)' },
      { name: 'monthly_traffic_goal', label: 'Monthly Traffic Goal', type: 'number', placeholder: 'e.g., 10000' },
      { name: 'current_ranking', label: 'Current Google Ranking', type: 'text', placeholder: 'e.g., Page 2-3' },
      { name: 'local_seo', label: 'Local SEO Focus', type: 'boolean', default: false },
      { name: 'content_strategy', label: 'Content Strategy', type: 'select', options: ['Blog Posts', 'Landing Pages', 'Product Pages', 'All'] },
    ]
  },
  software_development: {
    fields: [
      { name: 'tech_stack', label: 'Tech Stack', type: 'text', placeholder: 'e.g., React, Node.js, PostgreSQL' },
      { name: 'repository_url', label: 'Repository URL', type: 'text', placeholder: 'https://github.com/...' },
      { name: 'deployment_env', label: 'Deployment Environment', type: 'select', options: ['AWS', 'Azure', 'GCP', 'Heroku', 'Vercel', 'Self-hosted'] },
      { name: 'sprint_duration', label: 'Sprint Duration (weeks)', type: 'select', options: ['1', '2', '3', '4'] },
      { name: 'has_api', label: 'API Development', type: 'boolean', default: true },
      { name: 'testing_required', label: 'Testing Requirements', type: 'select', options: ['Unit Tests', 'Integration Tests', 'E2E Tests', 'All', 'None'] },
    ]
  },
  branding: {
    fields: [
      { name: 'brand_type', label: 'Brand Type', type: 'select', options: ['New Brand', 'Rebrand', 'Brand Refresh', 'Brand Extension'] },
      { name: 'deliverables', label: 'Deliverables', type: 'textarea', placeholder: 'Logo, Business Cards, Brand Guidelines...' },
      { name: 'industry', label: 'Industry', type: 'text', placeholder: 'e.g., Technology, Healthcare' },
      { name: 'target_audience', label: 'Target Audience', type: 'text', placeholder: 'e.g., Young professionals 25-40' },
      { name: 'color_preferences', label: 'Color Preferences', type: 'text', placeholder: 'e.g., Blue, Professional' },
      { name: 'competitors_ref', label: 'Competitor References', type: 'textarea', placeholder: 'Brands to reference or avoid' },
    ]
  },
  marketing: {
    fields: [
      { name: 'campaign_type', label: 'Campaign Type', type: 'select', options: ['Social Media', 'Email', 'PPC', 'Content Marketing', 'Full Funnel'] },
      { name: 'platforms', label: 'Platforms', type: 'text', placeholder: 'e.g., Facebook, Instagram, Google Ads' },
      { name: 'monthly_budget', label: 'Monthly Ad Budget ($)', type: 'number', placeholder: 'e.g., 5000' },
      { name: 'target_cpa', label: 'Target CPA ($)', type: 'number', placeholder: 'e.g., 50' },
      { name: 'conversion_goal', label: 'Conversion Goal', type: 'select', options: ['Lead Generation', 'Sales', 'App Installs', 'Brand Awareness'] },
      { name: 'reporting_frequency', label: 'Reporting Frequency', type: 'select', options: ['Weekly', 'Bi-weekly', 'Monthly'] },
    ]
  },
  dme_medical: {
    fields: []  // DME Medical has its own dedicated project page — no inline form fields
  },
  other: {
    fields: [
      { name: 'project_scope', label: 'Project Scope', type: 'textarea', placeholder: 'Describe the project scope...' },
      { name: 'key_deliverables', label: 'Key Deliverables', type: 'textarea', placeholder: 'List main deliverables...' },
      { name: 'special_requirements', label: 'Special Requirements', type: 'textarea', placeholder: 'Any special requirements...' },
    ]
  }
};

const Projects = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client_id: '',
    organization_id: '',
    project_type: 'web_design',
    due_date: '',
    budget: '',
    custom_fields: {},
    tags: []
  });
  const [tagInput, setTagInput] = useState('');
  const [dueDate, setDueDate] = useState(null);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchData();
  }, []);

  // Initialize custom fields when project type changes
  useEffect(() => {
    const template = projectTemplates[formData.project_type];
    if (template) {
      const defaultFields = {};
      template.fields.forEach(field => {
        if (field.default !== undefined) {
          defaultFields[field.name] = field.default;
        } else if (field.type === 'boolean') {
          defaultFields[field.name] = false;
        } else {
          defaultFields[field.name] = '';
        }
      });
      setFormData(prev => ({ ...prev, custom_fields: defaultFields }));
    }
  }, [formData.project_type]);

  const fetchData = async () => {
    try {
      const [projectsRes, clientsRes, tagsRes] = await Promise.all([
        axios.get(`${API_URL}/projects`, getAuthHeaders()),
        axios.get(`${API_URL}/clients`, getAuthHeaders()),
        axios.get(`${API_URL}/projects/tags`, getAuthHeaders()).catch(() => ({ data: { tags: [] } }))
      ]);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
      setAvailableTags(tagsRes.data?.tags || []);

      if (isSuperAdmin) {
        const orgsRes = await axios.get(`${API_URL}/tenants`, getAuthHeaders());
        setOrganizations(orgsRes.data);
      }
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        due_date: dueDate ? dueDate.toISOString() : null,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        organization_id: isSuperAdmin ? formData.organization_id : user.organization_id
      };
      const res = await axios.post(`${API_URL}/projects`, submitData, getAuthHeaders());
      toast.success('Project created successfully');
      setIsCreateOpen(false);
      resetForm();
      // DME Medical projects go to the dedicated page
      if (submitData.project_type === 'dme_medical' && res.data?.id) {
        navigate(`/projects/dme/${res.data.id}`);
      } else {
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        name: formData.name,
        description: formData.description,
        due_date: dueDate ? dueDate.toISOString() : null,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        custom_fields: formData.custom_fields
      };
      await axios.put(`${API_URL}/projects/${selectedProject.id}`, submitData, getAuthHeaders());
      toast.success('Project updated');
      setIsEditOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update project');
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await axios.delete(`${API_URL}/projects/${projectId}`, getAuthHeaders());
      toast.success('Project deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const openEditModal = (project) => {
    setSelectedProject(project);
    setFormData({
      name: project.name,
      description: project.description || '',
      client_id: project.client_id,
      organization_id: project.organization_id,
      project_type: project.project_type,
      due_date: project.due_date || '',
      budget: project.budget || '',
      custom_fields: project.custom_fields || {},
      tags: project.tags || []
    });
    setDueDate(project.due_date ? new Date(project.due_date) : null);
    setTagInput('');
    setIsEditOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      client_id: '',
      organization_id: '',
      project_type: 'web_design',
      due_date: '',
      budget: '',
      custom_fields: {},
      tags: []
    });
    setDueDate(null);
    setTagInput('');
  };

  const addTag = (tag) => {
    const t = tag.trim().toLowerCase().replace(/\s+/g, '-');
    if (t && !formData.tags.includes(t)) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, t] }));
    }
    setTagInput('');
  };

  const removeTag = (tag) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
    }
  };

  const updateCustomField = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      custom_fields: {
        ...prev.custom_fields,
        [fieldName]: value
      }
    }));
  };

  const renderCustomFields = () => {
    const template = projectTemplates[formData.project_type];
    if (!template || !template.fields || template.fields.length === 0) return null;

    return (
      <div className="space-y-4 pt-4 border-t border-slate-100">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          {(() => {
            const TypeIcon = projectTypes.find(t => t.value === formData.project_type)?.icon || LayoutTemplate;
            return <TypeIcon size={16} />;
          })()}
          {projectTypes.find(t => t.value === formData.project_type)?.label} Details
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {template.fields.map(field => (
            <div key={field.name} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
              {field.type === 'boolean' ? (
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <Label htmlFor={field.name} className="cursor-pointer">{field.label}</Label>
                  <Switch
                    id={field.name}
                    checked={formData.custom_fields[field.name] || false}
                    onCheckedChange={(checked) => updateCustomField(field.name, checked)}
                  />
                </div>
              ) : field.type === 'select' ? (
                <div className="space-y-2">
                  <Label>{field.label}</Label>
                  <Select
                    value={formData.custom_fields[field.name] || ''}
                    onValueChange={(value) => updateCustomField(field.name, value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {field.options.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : field.type === 'textarea' ? (
                <div className="space-y-2">
                  <Label>{field.label}</Label>
                  <Textarea
                    value={formData.custom_fields[field.name] || ''}
                    onChange={(e) => updateCustomField(field.name, e.target.value)}
                    placeholder={field.placeholder}
                    rows={2}
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>{field.label}</Label>
                  <Input
                    type={field.type}
                    value={formData.custom_fields[field.name] || ''}
                    onChange={(e) => updateCustomField(field.name, e.target.value)}
                    placeholder={field.placeholder}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const getTypeInfo = (type) => projectTypes.find(t => t.value === type) || projectTypes[5];

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    const matchesTag = !tagFilter || (project.tags || []).includes(tagFilter);
    return matchesSearch && matchesStatus && matchesTag;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Projects
          </h1>
          <p className="text-slate-500 mt-1">Manage your web design and SEO projects</p>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="create-project-btn">
              <Plus size={18} className="mr-2" />
              New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Create New Project</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Project Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Website Redesign"
                  required
                  data-testid="project-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Project description..."
                  rows={2}
                  data-testid="project-description-input"
                />
              </div>

              {isSuperAdmin && (
                <div className="space-y-2">
                  <Label>Tenant *</Label>
                  <Select
                    value={formData.organization_id}
                    onValueChange={(value) => setFormData({ ...formData, organization_id: value, client_id: '' })}
                  >
                    <SelectTrigger data-testid="project-tenant-select">
                      <SelectValue placeholder="Select tenant" />
                    </SelectTrigger>
                    <SelectContent>
                      {organizations.map(org => (
                        <SelectItem key={org.id} value={org.id}>{org.company_name || org.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Client *</Label>
                <Select
                  value={formData.client_id}
                  onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                >
                  <SelectTrigger data-testid="project-client-select">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.filter(c => 
                      isSuperAdmin ? c.organization_id === formData.organization_id : true
                    ).map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.company || client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Project Type *</Label>
                  <Select
                    value={formData.project_type}
                    onValueChange={(value) => setFormData({ ...formData, project_type: value })}
                  >
                    <SelectTrigger data-testid="project-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {projectTypes.map(type => {
                        const TypeIcon = type.icon;
                        return (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2">
                              <TypeIcon size={14} />
                              {type.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Budget ($)</Label>
                  <Input
                    type="number"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    placeholder="0.00"
                    data-testid="project-budget-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Due Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dueDate ? format(dueDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={dueDate}
                      onSelect={setDueDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Custom Fields based on Project Type */}
              {renderCustomFields()}

              {/* Tags */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Tag size={14} className="text-slate-500" /> Tags</Label>
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    placeholder="Type a tag and press Enter (e.g. seo, priority)"
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" size="sm" onClick={() => addTag(tagInput)}>Add</Button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {formData.tags.map(tag => (
                      <span key={tag} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full border border-blue-200">
                        #{tag}
                        <button type="button" onClick={() => removeTag(tag)} className="hover:text-red-500 transition-colors"><X size={11} /></button>
                      </span>
                    ))}
                  </div>
                )}
                {availableTags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    <span className="text-xs text-slate-400">Existing:</span>
                    {availableTags.filter(t => !formData.tags.includes(t)).map(tag => (
                      <button key={tag} type="button" onClick={() => addTag(tag)}
                        className="text-xs px-2 py-0.5 rounded-full border border-dashed border-slate-300 text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-colors">
                        +#{tag}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setIsCreateOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800">
                  Create Project
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-projects-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="status-filter-select">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statusOptions.map(status => (
                  <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Tag filter chips */}
          {availableTags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
              <span className="text-xs text-slate-400 flex items-center gap-1 mr-1">
                <Tag size={12} /> Filter by tag:
              </span>
              <button
                onClick={() => setTagFilter('')}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  !tagFilter
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                All
              </button>
              {availableTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => setTagFilter(tagFilter === tag ? '' : tag)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    tagFilter === tag
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  #{tag}
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <FolderKanban size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700">No projects found</h3>
            <p className="text-slate-500 mt-1">Create your first project to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProjects.map(project => {
            const typeInfo = getTypeInfo(project.project_type);
            const TypeIcon = typeInfo.icon;
            return (
              <Card 
                key={project.id} 
                className="border-slate-200 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(project.project_type === 'dme_medical' ? `/projects/dme/${project.id}` : `/projects/${project.id}`)}
                data-testid={`project-card-${project.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className={`p-2 rounded-lg ${typeInfo.color}`}>
                      <TypeIcon size={20} />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(project.project_type === 'dme_medical' ? `/projects/dme/${project.id}` : `/projects/${project.id}`); }}>
                          <Eye size={14} className="mr-2" />
                          View
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditModal(project); }}>
                          <Pencil size={14} className="mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={(e) => { e.stopPropagation(); handleDelete(project.id); }}
                          className="text-red-600"
                        >
                          <Trash2 size={14} className="mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  <h3 className="font-semibold text-slate-900 mt-3">{project.name}</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    {clients.find(c => c.id === project.client_id)?.company || 
                     clients.find(c => c.id === project.client_id)?.name || 'Unknown Client'}
                  </p>

                  {/* Tag badges */}
                  {project.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {project.tags.map(tag => (
                        <span
                          key={tag}
                          onClick={(e) => { e.stopPropagation(); setTagFilter(tag); }}
                          className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full border border-blue-100 hover:bg-blue-100 transition-colors cursor-pointer"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between mt-4">
                    <span className={`status-badge status-${project.status}`}>
                      {statusOptions.find(s => s.value === project.status)?.label}
                    </span>
                    {project.due_date && (
                      <span className="text-xs text-slate-500">
                        Due {format(new Date(project.due_date), 'MMM d')}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit Project Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Edit Project</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Project Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Budget ($)</Label>
                <Input
                  type="number"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dueDate ? format(dueDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={dueDate}
                      onSelect={setDueDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Custom Fields based on Project Type */}
            {renderCustomFields()}

            {/* Tags */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1"><Tag size={14} className="text-slate-500" /> Tags</Label>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  placeholder="Type a tag and press Enter"
                  className="flex-1"
                />
                <Button type="button" variant="outline" size="sm" onClick={() => addTag(tagInput)}>Add</Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {formData.tags.map(tag => (
                    <span key={tag} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full border border-blue-200">
                      #{tag}
                      <button type="button" onClick={() => removeTag(tag)} className="hover:text-red-500 transition-colors"><X size={11} /></button>
                    </span>
                  ))}
                </div>
              )}
              {availableTags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  <span className="text-xs text-slate-400">Existing:</span>
                  {availableTags.filter(t => !formData.tags.includes(t)).map(tag => (
                    <button key={tag} type="button" onClick={() => addTag(tag)}
                      className="text-xs px-2 py-0.5 rounded-full border border-dashed border-slate-300 text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-colors">
                      +#{tag}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Projects;
